import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../utils/api';
import LucideIcon from './LucideIcon';
import { getQuestIcon } from '../utils/iconMapper';

interface Quest {
  questId: string;
  title: string;
  description: string;
  icon: string;
  type: string;
  totalLevels: number;
  currentLevel: number;
  completedLevels: number;
  pointsEarned: number;
  completed: boolean;
}

// Static quest definitions -- keep in sync with backend/missions-data.json and
// backend/mission-*.json (questId, title, description)
const QUEST_DEFINITIONS = [
  { questId: 'galactic-spy', title: 'Galactic Spy Network', description: 'Master espionage and intelligence gathering through covert operations', icon: 'Eye', type: 'Espionage & Stealth', totalLevels: 10 },
  { questId: 'ice-planet-escape', title: 'Ice Planet Escape', description: 'Survive harsh conditions and escape from a frozen world', icon: 'Snowflake', type: 'Survival & Strategy', totalLevels: 10 },
  { questId: 'archive-hunt', title: 'The Archive Hunt', description: 'Decode ancient artifacts and uncover lost knowledge', icon: 'Map', type: 'Archaeology & Puzzles', totalLevels: 10 },
  { questId: 'sensei-trials', title: "The Sensei's Trials", description: 'Prove your mastery of prompting through challenging trials', icon: 'Sword', type: 'Discipline & Wisdom', totalLevels: 10 },
  { questId: 'time-paradox', title: 'The Time Paradox', description: 'Navigate temporal anomalies and prevent timeline collapse', icon: 'Clock', type: 'Logic & Causality', totalLevels: 10 },
];

const panel: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22,
};

const PromptQuest: React.FC = () => {
  const [questProgress, setQuestProgress] = useState<Record<string, { completedLevels: number; pointsEarned: number }>>({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchQuestProgress = async () => {
      const questData: Record<string, { completedLevels: number; pointsEarned: number }> = {};
      await Promise.all(
        QUEST_DEFINITIONS.map(async (quest) => {
          try {
            const response = await API.get(`/quests/${quest.questId}/progress`);
            questData[quest.questId] = { completedLevels: response.completedLevels?.length || 0, pointsEarned: response.pointsEarned || 0 };
          } catch (error) {
            questData[quest.questId] = { completedLevels: 0, pointsEarned: 0 };
          }
        })
      );
      setQuestProgress(questData);
      setLoading(false);
    };
    fetchQuestProgress();
  }, []);

  const quests: Quest[] = QUEST_DEFINITIONS.map((def) => {
    const progress = questProgress[def.questId] || { completedLevels: 0, pointsEarned: 0 };
    return {
      ...def,
      currentLevel: progress.completedLevels + 1,
      completedLevels: progress.completedLevels,
      pointsEarned: progress.pointsEarned,
      completed: progress.completedLevels >= def.totalLevels,
    };
  });

  const showLoading = loading && Object.keys(questProgress).length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1000 }}>
      <div className="ws-fade-up" style={panel}>
        <h1 className="ws-display" style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Missions</h1>
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>Story-based challenges that put your prompt-engineering skills to work.</p>
        {showLoading && <p className="ws-mono" style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-tertiary)' }}>Loading progress…</p>}
      </div>

      {/* Sub-destinations of Missions (docs/build-plan/03-core-learning-app.md):
          the entry point into the 50 Tier-2/3 lessons (LangChain/LangGraph/
          CrewAI/Microsoft Agent Framework/Production) that have no other
          discoverable route in the app. */}
      <div className="ws-quest-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div
          className="ws-fade-up"
          role="button"
          tabIndex={0}
          onClick={() => navigate('/frameworks')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/frameworks'); } }}
          style={{ ...panel, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}
        >
          <span style={{ flex: '0 0 auto', color: 'var(--ember-strong)' }}><LucideIcon name="topic-coding-debugging" size={32} /></span>
          <div>
            <h2 className="ws-display" style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Frameworks</h2>
            <p style={{ margin: '3px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
              LangChain, LangGraph, CrewAI, and Microsoft Agent Framework — build real agents.
            </p>
          </div>
        </div>
        <div
          className="ws-fade-up"
          role="button"
          tabIndex={0}
          onClick={() => navigate('/production')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/production'); } }}
          style={{ ...panel, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}
        >
          <span style={{ flex: '0 0 auto', color: 'var(--ember-strong)' }}><LucideIcon name="graded-info" size={32} /></span>
          <div>
            <h2 className="ws-display" style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Production & Observability</h2>
            <p style={{ margin: '3px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
              Tracing, evaluation, and guardrails — ship an agent you can trust.
            </p>
          </div>
        </div>
      </div>

      <div className="ws-quest-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {quests.map((quest, i) => (
          <div key={quest.questId} className="ws-fade-up" style={{ ...panel, animationDelay: `${60 + i * 20}ms`, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 14 }}>
              <span style={{ flex: '0 0 auto', color: 'var(--ember-strong)' }}><LucideIcon name={getQuestIcon(quest.questId)} size={36} /></span>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <h2 className="ws-display" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{quest.title}</h2>
                  {quest.completed && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: 'var(--success)', background: 'oklch(0.65 0.15 155 / 0.14)', border: '1px solid var(--success)', borderRadius: 999, padding: '2px 8px' }}>
                      <LucideIcon name="status-check" size={11} /> Complete
                    </span>
                  )}
                </div>
                <p className="ws-mono" style={{ margin: '3px 0 0', fontSize: 11, fontWeight: 700, color: 'var(--ember-strong)' }}>{quest.type}</p>
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{quest.description}</p>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 6 }}>
                <span>{quest.completedLevels}/{quest.totalLevels} levels</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><LucideIcon name="points" size={13} /> {quest.pointsEarned} credits</span>
              </div>
              <div style={{ height: 7, borderRadius: 999, background: 'var(--surface-sunken)', border: '1px solid var(--border)', overflow: 'hidden' }}>
                <div style={{ width: `${(quest.completedLevels / quest.totalLevels) * 100}%`, height: '100%', borderRadius: 999, background: 'var(--ember)' }} />
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate(`/prompt-quest/${quest.questId}`)}
              className="btn-live ws-display"
              style={{ marginTop: 'auto', padding: '11px 20px', borderRadius: 10, border: 'none', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', background: 'var(--ember)', color: 'white' }}
            >
              {quest.completedLevels === 0 ? 'Start mission' : quest.completed ? 'Replay mission' : 'Continue mission'}
            </button>
          </div>
        ))}
      </div>

      <div className="ws-fade-up" style={{ ...panel, background: 'var(--ember-soft)', borderColor: 'var(--ember)' }}>
        <h3 className="ws-display" style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>How it works</h3>
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            'Each mission has 10 progressive levels',
            'Complete a level to unlock the next one',
            'Your response becomes a hint for the next level',
            'Earn 15 points per level, plus 50 bonus for completing the mission',
          ].map((line) => (
            <li key={line} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
              <span style={{ color: 'var(--ember-strong)' }}>•</span>{line}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default PromptQuest;
